import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Mail,
  BarChart3,
  ShieldBan,
  MessageSquare,
  MessageCircle,
  Shield,
  CheckCheck,
  ChevronRight,
  ChevronLeft,
  Moon,
  Sun,
  Search,
  LogOut,
  Facebook,
  Instagram,
  MessagesSquare,
  Video,
  Users,
  PhoneOff,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { useAuthStore } from '@/stores/useAuthStore';
import styles from './Sidebar.module.css';

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { title: 'Dashboard', href: ROUTES.dashboard, icon: LayoutDashboard },
      { title: 'Manage Leads', href: ROUTES.leadsManage, icon: Users },
    ],
  },
  {
    label: 'Outreach',
    items: [
      { title: 'Email Campaigns', href: ROUTES.emailOutreach, icon: Mail },
      { title: 'WhatsApp Campaigns', href: ROUTES.whatsappOutreach, icon: MessageSquare },
      { title: 'WhatsApp Inbox', href: ROUTES.whatsappInbox, icon: MessageCircle },
      { title: 'Messenger Inbox', href: ROUTES.messengerInbox, icon: Facebook },
      { title: 'Instagram Inbox', href: ROUTES.instagramInbox, icon: Instagram },
      { title: 'TikTok Comments', href: ROUTES.tiktokComments, icon: Video },
    ],
  },
  {
    label: 'Analytics',
    items: [
      { title: 'Email History', href: ROUTES.mailingHistory, icon: BarChart3 },
      { title: 'WhatsApp History', href: ROUTES.whatsappHistory, icon: CheckCheck },
      { title: 'Social Comments', href: ROUTES.socialComments, icon: MessagesSquare },
    ],
  },
  {
    label: 'Admin',
    items: [
      { title: 'Users', href: ROUTES.adminUsers, icon: Shield },
      { title: 'Email Blacklist', href: ROUTES.emailBlacklist, icon: ShieldBan },
      { title: 'Phone Blacklist', href: ROUTES.phoneBlacklist, icon: PhoneOff },
    ],
  },
];

interface SidebarProps {
  className?: string;
  onClose?: () => void;
}

export function Sidebar({ className, onClose }: SidebarProps) {
  const location = useLocation();
  const logOut = useAuthStore((s) => s.logOut);
  const [collapsed, setCollapsed] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem('theme');
    if (stored) return stored === 'dark';
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark((prev) => !prev);

  return (
    <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''} ${className || ''}`}>
      {!onClose && (
        <button onClick={() => setCollapsed(!collapsed)} className={styles.collapseToggle}>
          {collapsed ? (
            <ChevronRight className={styles.collapseIcon} />
          ) : (
            <ChevronLeft className={styles.collapseIcon} />
          )}
        </button>
      )}

      <div className={`${styles.logoArea} ${collapsed ? styles.logoAreaCollapsed : ''}`}>
        <div className={styles.logoRow}>
          <div className={styles.logoIcon}>
            <Search className={styles.logoSearchIcon} />
          </div>
          {!collapsed && <span className={styles.logoText}>Leads Manager</span>}
        </div>
      </div>

      <nav className={styles.nav}>
        {navSections.map((section, sectionIdx) => (
          <div key={section.label} className={styles.navSection}>
            {sectionIdx > 0 && <div className={styles.navSectionDivider} />}
            {!collapsed && <span className={styles.navSectionLabel}>{section.label}</span>}
            {section.items.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  title={collapsed ? item.title : ''}
                  onClick={() => onClose?.()}
                  className={`${styles.navItem} ${isActive ? styles.navItemActive : ''} ${collapsed ? styles.navItemCollapsed : ''}`}
                >
                  <div className={styles.navItemInner}>
                    <item.icon
                      className={`${styles.navIcon} ${isActive ? styles.navIconActive : ''}`}
                    />
                    {!collapsed && <span className={styles.navLabel}>{item.title}</span>}
                  </div>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div className={styles.themeToggleArea}>
        <button
          onClick={toggleTheme}
          className={`${styles.themeButton} ${collapsed ? styles.themeButtonCollapsed : ''}`}
          title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {isDark ? (
            <Sun className={styles.themeIconSun} />
          ) : (
            <Moon className={styles.themeIconMoon} />
          )}
          {!collapsed && <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>}
        </button>
        <button
          onClick={() => logOut()}
          className={`${styles.logoutButton} ${collapsed ? styles.logoutButtonCollapsed : ''}`}
          title="Log Out"
        >
          <LogOut className={styles.logoutIcon} />
          {!collapsed && <span>Log Out</span>}
        </button>
      </div>
    </aside>
  );
}
